'use client';

import { asText, Content } from '@prismicio/client';
import { SubmitEvent, useRef, useState } from 'react';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Text,
  Button,
  Drawer,
  Heading,
  TextState,
  EmptyState,
  DrawerBody,
  DrawerHeader,
  CallToActionInput,
  Banner,
} from '@preventx/oxygen';
import {
  SiteLogo,
  SiteBrand,
  HttpStatusCodeEnum,
  ReferrerConfiguration,
  ReferrerBrandLogoOverride,
} from '@preventx/prismic-services';

// Types
import { ISOLanguageKeys } from '@/types/language';
import { DataStructureResponse } from '@/types/siteSearch/mapper';

// Constants
import {
  SearchMaximumCharacterLength,
  SearchMinimumCharacterLength,
} from '@/constants/algolia';
import { TotalReplacementKey } from '@/constants/configuration';

// API
import { get, post } from '@/api/siteSearch/client';

// Components
import { ClinicsContent } from '@/components/Site/Search/ClinicsContent';
import { StandardContent } from '@/components/Site/Search/StandardContent';

const ButtonIdentifier = 'site-header-search-button';
const PopoverDrawerTargetIdentifier = 'site-header-search-container';

type SearchContainerProps = {
  brand: SiteBrand;
  language: ISOLanguageKeys;
  referrers: ReferrerConfiguration[];
  containerContent: Content.SiteHeaderDocumentData;
};

export const SearchContainer = ({
  brand,
  language,
  referrers,
  containerContent,
}: SearchContainerProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [validate, setValidate] = useState<boolean>(false);
  const [totalResults, setTotalResults] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [queryID, setQueryID] = useState<string | undefined>(undefined);

  const [clinics, setClinics] = useState<DataStructureResponse[]>([]);
  const [content, setContent] = useState<DataStructureResponse[]>([]);

  const [filter, setFilter] = useState<TextState>({
    value: '',
    isValid: false,
  });

  const {
    search_clear_label,
    search_button_label,
    search_error_message,
    search_no_results_title,
    search_total_result_label,
    search_no_results_message,
    search_default_state_title,
    search_default_state_message,
  } = containerContent;

  const onSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!filter.isValid) {
      setValidate(true);

      return;
    }

    setErrorMessage('');
    setIsProcessing(true);

    const { data, statusCode } = await get({
      payload: { filter: filter.value },
    });

    if (!data || statusCode !== HttpStatusCodeEnum.OK) {
      setIsProcessing(false);
      setErrorMessage(asText(search_error_message));

      return;
    }

    const { total, content, clinics, queryID } = data;

    setContent(content);
    setClinics(clinics);
    setQueryID(queryID);

    const totalResultsLabel = asText(search_total_result_label);
    setTotalResults(
      totalResultsLabel.replace(TotalReplacementKey, total.toString()),
    );

    setHasSearched(true);
    setIsProcessing(false);
  };

  const onReset = () => {
    setContent([]);
    setClinics([]);
    setTotalResults('');
    setErrorMessage('');
    setHasSearched(false);
    setFilter({ value: '', isValid: false });
  };

  const onClick = async (objectIDs: string[]) => {
    ref.current?.hidePopover();

    // Note : This isn't critical to the workflow so can be handled silently in the background
    await post({
      payload: {
        type: 'click',
        queryID: queryID,
        objectIDs: objectIDs,
      },
    });
  };

  return (
    <>
      <Button
        type="button"
        variant="icon"
        onClick={() => []}
        id={ButtonIdentifier}
        tooltip={asText(search_button_label)}
        popoverTarget={PopoverDrawerTargetIdentifier}
        icon={{ definition: faSearch, position: 'icon-only' }}
      >
        {asText(search_button_label)}
      </Button>

      <Drawer
        ref={ref}
        ariaLabelledBy=""
        position="block-start"
        id={PopoverDrawerTargetIdentifier}
      >
        <DrawerHeader
          closeLabel="Close"
          drawerId={PopoverDrawerTargetIdentifier}
        >
          <ReferrerBrandLogoOverride referrers={referrers}>
            <SiteLogo logo={brand}></SiteLogo>
          </ReferrerBrandLogoOverride>
        </DrawerHeader>
        <DrawerBody>
          <Box addFlow width="w-100%" component="form" onSubmit={onSubmit}>
            <CallToActionInput>
              <Text
                type="text"
                state={filter}
                autoComplete="off"
                validate={validate}
                onChange={setFilter}
                setValidate={setValidate}
                id="site-header-search-input"
                name="site-header-search-input"
                ariaLabel={asText(containerContent.search_input_label)}
                placeholder={asText(containerContent.search_input_label)}
                validationRules={{
                  required: true,
                  maxLength: SearchMaximumCharacterLength,
                  minLength: SearchMinimumCharacterLength,
                }}
              />

              <Button variant="solid" colour="primary" disabled={isProcessing}>
                {asText(containerContent.search_submit_label)}
              </Button>
            </CallToActionInput>

            {errorMessage && (
              <Banner
                colour="negative"
                dismissLabel="Dismiss"
                onDismiss={() => setErrorMessage('')}
              >
                {errorMessage}
              </Banner>
            )}

            {(content.length > 0 || clinics.length > 0) && (
              <>
                <Box component="div" addRepel>
                  <Heading component="h3" addEyebrow>
                    {totalResults}
                  </Heading>
                  <Button
                    size="sm"
                    type="reset"
                    variant="outline"
                    onClick={onReset}
                  >
                    {asText(search_clear_label)}
                  </Button>
                </Box>

                <Box addFlow component="div">
                  {content.length > 0 && (
                    <StandardContent
                      onClick={onClick}
                      content={content}
                      language={language}
                      containerContent={containerContent}
                    />
                  )}

                  {clinics.length > 0 && (
                    <ClinicsContent
                      onClick={onClick}
                      content={clinics}
                      language={language}
                      containerContent={containerContent}
                    />
                  )}
                </Box>
              </>
            )}

            {!hasSearched && (
              <EmptyState
                icon={faSearch}
                title={asText(search_default_state_title)}
                caption={asText(search_default_state_message)}
              />
            )}

            {content.length === 0 && clinics.length === 0 && hasSearched && (
              <EmptyState
                icon={faSearch}
                title={asText(search_no_results_title)}
                caption={asText(search_no_results_message)}
              />
            )}
          </Box>
        </DrawerBody>
      </Drawer>
    </>
  );
};
